import { colors } from './colors';

/**
 * Anything commented out is a new color token. Figma color naming matches the comments.
 */

const color_tokens = {
  // background colors
  surface: {
    base: {
      /** tan-bg */
      background: colors.stone[50],
      /** bg-0 */
      surface: colors.white,
      /** bg-3 */
      variant_surface: colors.neutral[200],
      /** bg-2 */
      variant_surface_2: colors.neutral[100],
      /** bg-1 - new color token */
      variant_surface_3: colors.neutral[50],
      /** bg-elevated - new color token */
      background_elevated: colors.white,
      /** bg-contrast - new color token */
      background_contrast: colors.black,
      /** primary-blue-bg */
      primary: colors.blue[500],
      /** no match - used on console */
      success: colors.green[500],
      /** green-histogram-bg - new color token */
      green_histogram: colors.green[500],
      /** primary-yellow-bg */
      warning: colors.yellow[500],
      /** primary-red-bg */
      danger: colors.red[500],
      /** red-histogram-bg - new color token */
      red_histogram: colors.red[500],
      /** new color token */
      disabled: colors.neutral[100],
      /** bg-4 */
      neutral: colors.neutral[500],
      /** neutral-histogram-bg - new color token */
      neutral_histogram: colors.neutral[500],
      backdrop: `rgba(0, 0, 0, 0.1)`,
      // States
      hover: {
        /** bg-0-hover */
        neutral: colors.neutral[100],
        neutral_variant: colors.neutral[200],
        /** primary-blue-bg-hover */
        primary: colors.blue[600],
        /** primary-red-bg-hover */
        danger: colors.red[600],
      },
      active: {
        neutral: colors.neutral[200],
        neutral_variant: colors.neutral[400],
        primary: colors.blue[600],
        danger: colors.red[700],
      },
    },
    chart: {
      primary: '#99B8F5',
      secondary: '#79B6C2',
      blue_300: colors.blue[300],
      blue_400: colors.blue[400],
      blue_500: colors.blue[500],
      blue_600: colors.blue[600],
      danger: colors.red[400],
      success: colors.green[500],
    },
    container: {
      neutral: colors.neutral[200],
      /** blue-badge-bg */
      primary: colors.blue[50],
      /** success-badge-bg */
      success: colors.green[50],
      /** caution-badge-bg */
      warning: colors.yellow[100],
      /** danger-badge-bg */
      danger: colors.red[50],
      highlight: colors.yellow[200],
      // States
      hover: {
        neutral: colors.neutral[300],
        /** blue-badge-bg-hover */
        primary: colors.blue[100],
        success: colors.green[100],
        warning: colors.yellow[300],
        danger: colors.red[100],
      },
      active: {
        neutral: colors.neutral[400],
        primary: colors.blue[200],
        success: colors.green[200],
        warning: colors.yellow[400],
        danger: colors.red[200],
      },
    },
  },
  // foreground colors
  on: {
    neutral: {
      /** neutral-fg-1 */
      primary_neutral: colors.black,
      /** neutral-fg-3 */
      secondary_neutral: colors.stone[700],
      /** neutral-fg-2 */
      tertiary_neutral: colors.stone[800],
      /** primary-fg */
      primary: colors.blue[500],
      /** danger-fg */
      danger: colors.red[500],
      warning: colors.yellow[500],
      success: colors.green[500],
      /** disabled-fg */
      disabled: colors.stone[600],
      loading: colors.neutral[700],
    },
    code: {
      /** code-fg-boolean */
      boolean: colors.blue[600],
      /** code-fg-string */
      string: colors.green[600],
      /** code-fg-integer */
      number: colors.yellow[800],
      /** code-fg-null */
      null: colors.red[600],
    },
    hue: {
      /** on-hue-fg */
      primary: colors.white,
      success: colors.white,
      warning: colors.black,
      danger: colors.white,
      /** on-contrast-fg - new color token */
      contrast: colors.white,
    },
    hue_container: {
      /** primary-badge-fg */
      primary: colors.blue[500],
      primary_variant: colors.blue[300],
      /** neutral-fg-3 */
      neutral: colors.stone[700],
      secondary: colors.blue[300],
      /** success-badge-fg */
      success: colors.green[600],
      /** caution-badge-fg */
      warning: colors.yellow[900],
      /** danger-badge-fg */
      danger: colors.red[500],
    },
  },
  outline: {
    /** neutral-outline-1 */
    neutral: colors.neutral[200],
    /** neutral-outline-2 */
    neutral_2: colors.neutral[300],
    /** neutral-outline-3 */
    neutral_3: colors.neutral[500],
    /** contrast-line */
    contrast_line: colors.black,
    /** primary-badge-outline */
    primary: colors.blue[100],
    /** success-badge-outline */
    success: colors.green[100],
    /** caution-badge-outline */
    warning: colors.yellow[300],
    /** danger-badge-outline */
    danger: colors.red[100],
    // States
    hover: {
      neutral: colors.neutral[300],
      neutral_variant: colors.neutral[500],
      /** primary-badge-outline-hover */
      container_primary: colors.blue[200],
    },
    focus: {
      /** focused-outline */
      primary: colors.blue[300],
    },
    active: {
      neutral: colors.neutral[400],
      container_primary: colors.blue[300],
    },
  },
};

const elevation = {
  /** light-elevation-tooltip */
  '1': '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 4px 8px 0px rgba(0, 0, 0, 0.08)',
  /** light-elevation-menu */
  '2': '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 4px 8px 0px rgba(0, 0, 0, 0.08), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)',
  /** light-elevation-modal */
  '3': '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 8px 16px 0px rgba(0, 0, 0, 0.08), 0px 16px 32px 0px rgba(0, 0, 0, 0.08)',
};

const shadows = {
  primary: {
    /**
     * light-button-primary
     */
    base: `0px 1px 2px 0px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px ${colors.blue[600]}`,
    /**
     * used only on the DatePickerInput SelectableButton
     */
    selected: `0px 0.75px 0px 0px rgba(255, 255, 255, 0.20) inset, 0px 1px 2px 0px rgba(0, 0, 0, 0.32), 0px 0px 0px 1px ${colors.blue[100]}`,
    /**
     * light-button-primary-focused
     */
    focused: `0px 1px 2px 0px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px ${colors.blue[600]}, 0px 0px 0px 2px #FFF, 0px 0px 0px 4px ${colors.blue[300]}`,
  },
  danger: {
    base: `0px 0px 0px 1px rgba(0, 0, 0, 0.12), 0px 0px 0px 1px ${colors.red[600]}`,
    focused: `0px 0px 0px 1px rgba(0, 0, 0, 0.12), 0px 0px 0px 1px ${colors.red[600]}, 0px 0px 0px 2px #FFF, 0px 0px 0px 4px ${colors.red[300]}`,
  },
  neutral: {
    /**
     * light-button-neutral
     */
    base: '0px 0px 0px 1px rgba(0, 0, 0, 0.12), 0px 1px 2px 0px rgba(0, 0, 0, 0.04)',
    /**
     * light-button-neutral-highlight
     */
    highlight: `0px 1px 2px 0px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px ${colors.blue[500]}`,
    /**
     * light-button-neutral-focused
     */
    focused: `0px 1px 2px 0px rgba(0, 0, 0, 0.04), 0px 0px 0px 1px rgba(0, 0, 0, 0.12), 0px 0px 0px 2px #FFF, 0px 0px 0px 4px ${colors.blue[300]}`,
  },
  input: {
    /**
     * light-input-neutral
     */
    neutral: '0px 0px 0px 1px rgba(0, 0, 0, 0.12), 0px 1px 2px 0px rgba(0, 0, 0, 0.04) inset',
    /**
     * light-input-neutral-focused
     */
    focused: `0px 0px 0px 1px rgba(0, 0, 0, 0.12), 0px 1px 2px 0px rgba(0, 0, 0, 0.04) inset, 0px 0px 0px 2px #FFF, 0px 0px 0px 4px ${colors.blue[300]}`,
    /**
     * light-input-error
     */
    error: `0px 0px 0px 1px ${colors.red[500]}, 0px 1px 2px 0px rgba(0, 0, 0, 0.12) inset`,
  },
};

export default {
  colors: color_tokens,
  elevation,
  shadows,
};
